import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Phone, MapPin, Clock, User, CheckCircle, Pencil, AlertTriangle,
  Plus, Trash2, CalendarPlus, FileText, MessageSquare, ChevronRight, Minus
} from 'lucide-react'
import { besuche as besucheApi, offerten as offertenApi } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { besuchStatusBadge, defektZustandBadge, defektStatusBadge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { Input } from '../../components/ui/Input'
import { formatDatum, BESUCH_TYP_LABEL } from '../../lib/utils'
import { cn } from '../../lib/utils'
import { GERAETE_MODELLE } from '../../types'
import type { Besuch } from '../../types'

// ─── Angebot Modal ──────────────────────────────────────────────────────────

const GERAETE_PREISLISTE: Record<string, { kauf: number; miete: number }> = {
  'DS2 Home':          { kauf: 3190, miete: 149 },
  'DS2 Pro':           { kauf: 3690, miete: 179 },
  'DS2 Pro Cart':      { kauf: 4190, miete: 199 },
  'DS4':               { kauf: 6290, miete: 249 },
  'DS4+':              { kauf: 6990, miete: 279 },
  'DS4+ Mobility':     { kauf: 6990, miete: 279 },
}

interface AngebotPos {
  modell: string
  typ: 'kauf' | 'miete'
  menge: number
  preis: string
}

function AngebotModal({ open, onClose, kundeId }: {
  open: boolean
  onClose: () => void
  kundeId: string
}) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [gueltigBis, setGueltigBis] = useState('')
  const [notizen, setNotizen] = useState('')
  const [positionen, setPositionen] = useState<AngebotPos[]>([
    { modell: 'DS4', typ: 'kauf', menge: 1, preis: String(GERAETE_PREISLISTE['DS4']?.kauf ?? '') },
  ])

  const addPos = () => setPositionen((p) => [...p, { modell: 'DS4', typ: 'kauf', menge: 1, preis: String(GERAETE_PREISLISTE['DS4']?.kauf ?? '') }])
  const autoPreis = (modell: string, typ: 'kauf' | 'miete') =>
    String(GERAETE_PREISLISTE[modell]?.[typ] ?? '')
  const removePos = (i: number) => setPositionen((p) => p.filter((_, idx) => idx !== i))
  const updatePos = <K extends keyof AngebotPos>(i: number, k: K, v: AngebotPos[K]) =>
    setPositionen((p) => p.map((pos, idx) => idx === i ? { ...pos, [k]: v } : pos))

  const mutation = useMutation({
    mutationFn: () => offertenApi.create({
      kunde_id: kundeId,
      ersteller: user?.name ?? 'Unbekannt',
      gueltig_bis: gueltigBis || undefined,
      notizen: notizen || undefined,
      positionen: positionen.map((p) => ({
        beschreibung: `${p.modell} – ${p.typ === 'kauf' ? 'Kauf' : 'Miete / Monat'}`,
        menge: p.menge,
        einheit: p.typ === 'kauf' ? 'Stück' : 'Monat',
        einzelpreis: parseFloat(p.preis || '0'),
        rabatt: 0,
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['offerten'] })
      navigate('/offerten')
    },
  })

  const gesamtKauf = positionen
    .filter((p) => p.typ === 'kauf')
    .reduce((s, p) => s + p.menge * parseFloat(p.preis || '0'), 0)
  const gesamtMiete = positionen
    .filter((p) => p.typ === 'miete')
    .reduce((s, p) => s + p.menge * parseFloat(p.preis || '0'), 0)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Geräte-Angebot erstellen"
      footer={
        <div className="flex gap-2">
          <Button variant="outline" fullWidth onClick={onClose}>Abbrechen</Button>
          <Button
            fullWidth
            loading={mutation.isPending}
            disabled={positionen.length === 0}
            onClick={() => mutation.mutate()}
          >
            Angebot speichern
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Positionen */}
        <div className="space-y-3">
          {positionen.map((pos, i) => (
            <div key={i} className="bg-gray-50 rounded-2xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Position {i + 1}</p>
                {positionen.length > 1 && (
                  <button type="button" onClick={() => removePos(i)}
                    className="text-red-500 hover:text-red-700 p-1">
                    <Minus size={14} />
                  </button>
                )}
              </div>

              {/* Modell */}
              <Select
                label="Gerät"
                value={pos.modell}
                onChange={(e) => {
                  const newModell = e.target.value
                  setPositionen((p) => p.map((p2, idx) => idx === i
                    ? { ...p2, modell: newModell, preis: autoPreis(newModell, p2.typ) }
                    : p2
                  ))
                }}
                options={GERAETE_MODELLE.map((m) => ({ value: m, label: m }))}
              />

              {/* Kauf / Miete */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">Art</p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setPositionen((p) => p.map((p2, idx) => idx === i ? { ...p2, typ: 'kauf', preis: autoPreis(p2.modell, 'kauf') } : p2))}
                    className={cn('py-2 rounded-xl border-2 text-sm font-medium transition-all',
                      pos.typ === 'kauf' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-500'
                    )}>
                    💳 Kauf
                  </button>
                  <button type="button"
                    onClick={() => setPositionen((p) => p.map((p2, idx) => idx === i ? { ...p2, typ: 'miete', preis: autoPreis(p2.modell, 'miete') } : p2))}
                    className={cn('py-2 rounded-xl border-2 text-sm font-medium transition-all',
                      pos.typ === 'miete' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500'
                    )}>
                    📅 Miete
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Anzahl"
                  type="number"
                  min="1"
                  value={pos.menge.toString()}
                  onChange={(e) => updatePos(i, 'menge', parseInt(e.target.value) || 1)}
                />
                <Input
                  label={pos.typ === 'kauf' ? 'Preis CHF' : 'CHF / Monat'}
                  type="number"
                  min="0"
                  step="0.05"
                  placeholder="0.00"
                  value={pos.preis}
                  onChange={(e) => updatePos(i, 'preis', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>

        <Button variant="secondary" fullWidth size="sm" icon={<Plus size={14} />} onClick={addPos}>
          Weiteres Gerät hinzufügen
        </Button>

        {/* Zusammenfassung */}
        {(gesamtKauf > 0 || gesamtMiete > 0) && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 space-y-1">
            {gesamtKauf > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-blue-600">Kauf Total</span>
                <span className="font-bold text-blue-900">CHF {gesamtKauf.toFixed(2)}</span>
              </div>
            )}
            {gesamtMiete > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-blue-600">Miete / Monat</span>
                <span className="font-bold text-blue-900">CHF {gesamtMiete.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        <Input
          label="Angebot gültig bis"
          type="date"
          value={gueltigBis}
          onChange={(e) => setGueltigBis(e.target.value)}
        />
        <Textarea
          label="Notizen zum Angebot"
          placeholder="Rabatte, Konditionen, besondere Hinweise..."
          value={notizen}
          onChange={(e) => setNotizen(e.target.value)}
          rows={2}
        />

        {mutation.error && (
          <p className="text-sm text-red-600">{mutation.error.message}</p>
        )}
      </div>
    </Modal>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function BesuchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [angebotOpen, setAngebotOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['besuch', id],
    queryFn: () => besucheApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<Besuch>) => besucheApi.update(id!, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['besuch', id] }); setEditOpen(false) },
  })

  if (isLoading) return <><TopBar title="Besuch" backTo="/besuche" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/besuche" /><ErrorMessage message="Besuch nicht gefunden" /></>

  const isAbgeschlossen = data.status === 'abgeschlossen'

  return (
    <>
      <TopBar
        title={`Besuch – ${data.kunde_name}`}
        subtitle={`${formatDatum(data.datum)} · ${BESUCH_TYP_LABEL[data.typ] ?? data.typ}`}
        backTo="/besuche"
        rightAction={
          <Button variant="outline" size="sm" icon={<Pencil size={14} />} onClick={() => setEditOpen(true)}>
            Bearbeiten
          </Button>
        }
      />

      <div className="px-4 py-3 space-y-4 pb-8">

        {/* Status Banner */}
        <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            {besuchStatusBadge(data.status)}
            <span className="text-sm text-gray-600">{formatDatum(data.datum, 'EEEE, d. MMMM yyyy')}</span>
          </div>
          {!isAbgeschlossen && (
            <Button size="sm" icon={<CheckCircle size={14} />}
              onClick={() => updateMutation.mutate({ ...data, status: 'abgeschlossen' })}
              loading={updateMutation.isPending}>
              Abschliessen
            </Button>
          )}
        </div>

        {/* Kunde Info */}
        <Card>
          <CardHeader>
            <CardTitle>Kunde</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/kunden/${data.kunde_id}`)}>Profil</Button>
          </CardHeader>
          <div className="space-y-2">
            <p className="font-semibold text-gray-900">{data.kunde_firma ?? data.kunde_name}</p>
            {data.kunde_adresse && (
              <span className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin size={14} />{data.kunde_adresse}, {data.kunde_plz} {data.kunde_ort}
              </span>
            )}
            {data.kunde_telefon && (
              <a href={`tel:${data.kunde_telefon}`} className="flex items-center gap-2 text-sm text-blue-600">
                <Phone size={14} />{data.kunde_telefon}
              </a>
            )}
          </div>
        </Card>

        {/* Details */}
        <Card>
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {data.uhrzeit_start && (
              <div>
                <p className="text-gray-500 text-xs">Uhrzeit</p>
                <p className="font-medium flex items-center gap-1">
                  <Clock size={12} />{data.uhrzeit_start}{data.uhrzeit_ende ? ` – ${data.uhrzeit_ende}` : ''}
                </p>
              </div>
            )}
            <div>
              <p className="text-gray-500 text-xs">Accountmanager</p>
              <p className="font-medium flex items-center gap-1"><User size={12} />{data.techniker}</p>
            </div>
            {data.dauer_minuten ? (
              <div>
                <p className="text-gray-500 text-xs">Dauer</p>
                <p className="font-medium">{Math.floor(data.dauer_minuten / 60)}h {data.dauer_minuten % 60}min</p>
              </div>
            ) : null}
            <div>
              <p className="text-gray-500 text-xs">Typ</p>
              <p className="font-medium">{BESUCH_TYP_LABEL[data.typ] ?? data.typ}</p>
            </div>
          </div>
        </Card>

        {/* ─── Besprochene Punkte ── Hauptfeld ─── */}
        <Card>
          <CardHeader>
            <CardTitle><MessageSquare size={15} className="inline mr-1.5" />Besprochene Punkte</CardTitle>
          </CardHeader>
          {data.besprochene_punkte ? (
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{data.besprochene_punkte}</p>
          ) : (
            <p className="text-sm text-gray-400 italic text-center py-3">Keine Notizen erfasst</p>
          )}
        </Card>

        {/* Raport (weitere Felder) */}
        {(data.was_gemacht || data.naechste_schritte || data.zusammenfassung) && (
          <Card>
            <CardHeader><CardTitle>Raportdetails</CardTitle></CardHeader>
            <div className="space-y-4">
              {data.zusammenfassung && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Zusammenfassung</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.zusammenfassung}</p>
                </div>
              )}
              {data.was_gemacht && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Was wurde gemacht</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.was_gemacht}</p>
                </div>
              )}
              {data.naechste_schritte && (
                <div className="bg-amber-50 rounded-xl p-3">
                  <p className="text-xs text-amber-700 font-medium mb-1">Nächste Schritte</p>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{data.naechste_schritte}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* ─── Aktionen ─── */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => navigate(`/besuche/neu?kunde_id=${data.kunde_id}`)}
            className="flex items-center gap-2 p-3.5 bg-white border border-gray-200 rounded-2xl hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm"
          >
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <CalendarPlus size={17} className="text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Follow-up</p>
              <p className="text-xs text-gray-400">Neuer Besuch</p>
            </div>
          </button>

          <button
            onClick={() => setAngebotOpen(true)}
            className="flex items-center gap-2 p-3.5 bg-white border border-gray-200 rounded-2xl hover:border-purple-300 hover:bg-purple-50 transition-all shadow-sm"
          >
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
              <FileText size={17} className="text-purple-600" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Angebot</p>
              <p className="text-xs text-gray-400">Kauf oder Miete</p>
            </div>
          </button>
        </div>

        {/* Defekte */}
        <div>
          <CardHeader>
            <CardTitle><AlertTriangle size={16} className="inline mr-2" />Defekte ({data.defekte?.length ?? 0})</CardTitle>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />}
              onClick={() => navigate(`/defekte/neu?besuch_id=${id}&kunde_id=${data.kunde_id}`)}>
              Melden
            </Button>
          </CardHeader>
          {(data.defekte?.length ?? 0) > 0 ? (
            <div className="space-y-2">
              {data.defekte!.map((d) => (
                <Card key={d.id} clickable onClick={() => navigate(`/defekte/${d.id}`)} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.beschreibung}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {defektZustandBadge(d.zustand)}
                    {defektStatusBadge(d.status)}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card><p className="text-sm text-gray-500 text-center py-3">Keine Defekte gemeldet</p></Card>
          )}
        </div>

        {/* Delete */}
        <Button variant="danger" fullWidth size="sm" icon={<Trash2 size={14} />}
          onClick={() => {
            if (confirm('Besuch löschen?')) {
              besucheApi.delete(id!).then(() => {
                qc.invalidateQueries({ queryKey: ['besuche'] })
                navigate('/besuche')
              })
            }
          }}
        >
          Besuch löschen
        </Button>
      </div>

      {/* Edit Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Besuch bearbeiten"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button fullWidth loading={updateMutation.isPending}
              onClick={() => document.getElementById('besuch-edit-submit')?.click()}>
              Speichern
            </Button>
          </div>
        }
      >
        <EditBesuchForm data={data} onSubmit={(d) => updateMutation.mutate(d)} />
      </Modal>

      {/* Angebot Modal */}
      <AngebotModal
        open={angebotOpen}
        onClose={() => setAngebotOpen(false)}
        kundeId={data.kunde_id}
      />
    </>
  )
}

function EditBesuchForm({ data, onSubmit }: { data: Besuch; onSubmit: (d: Partial<Besuch>) => void }) {
  const [form, setForm] = useState({ ...data })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form) }} className="space-y-4">
      <Input label="Datum" type="date" value={form.datum?.slice(0, 10)} onChange={(e) => set('datum', e.target.value)} required />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Von" type="time" value={form.uhrzeit_start ?? ''} onChange={(e) => set('uhrzeit_start', e.target.value)} />
        <Input label="Bis" type="time" value={form.uhrzeit_ende ?? ''} onChange={(e) => set('uhrzeit_ende', e.target.value)} />
      </div>
      <Select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)}
        options={[
          { value: 'geplant', label: 'Geplant' },
          { value: 'unterwegs', label: 'Unterwegs' },
          { value: 'abgeschlossen', label: 'Abgeschlossen' },
          { value: 'abgesagt', label: 'Abgesagt' },
        ]}
      />
      <Select label="Typ" value={form.typ} onChange={(e) => set('typ', e.target.value)}
        options={[
          { value: 'service', label: 'Service' },
          { value: 'verkauf', label: 'Verkauf' },
          { value: 'installation', label: 'Installation' },
          { value: 'garantie', label: 'Garantie' },
          { value: 'demo', label: 'Demo' },
          { value: 'notfall', label: 'Notfall' },
        ]}
      />
      <Textarea label="Besprochene Punkte" value={form.besprochene_punkte ?? ''}
        onChange={(e) => set('besprochene_punkte', e.target.value)} rows={5} />
      <Textarea label="Was wurde gemacht" value={form.was_gemacht ?? ''}
        onChange={(e) => set('was_gemacht', e.target.value)} rows={3} />
      <Textarea label="Nächste Schritte" value={form.naechste_schritte ?? ''}
        onChange={(e) => set('naechste_schritte', e.target.value)} rows={3} />
      <button id="besuch-edit-submit" type="submit" className="hidden" />
    </form>
  )
}
